import GeneratingLottie from '@/assets/lottie/thinking.json';
import { useAuth } from '@/contexts/AuthContext';
import {
    agentService,
    EVENT_TYPES,
    FileItem,
    PulseMessage
} from '@common/api/agentService';
import {
    Button, Icon, Tooltip
} from '@common/index';
import { Cross2Icon } from '@radix-ui/react-icons';
import cls from 'classnames';
import dayjs from 'dayjs';
import Lottie from 'lottie-react';
import React, {
    useCallback,
    useEffect, useRef, useState
} from 'react';
import { Markdown } from '../Markdown';
import styles from './Chat.module.scss';

// 模拟消息类型
// Use PulseMessage from agentService instead of local interface
type ChatMessage = PulseMessage;

interface ChatModalProps {
    isOpen: boolean;
    onClose: () => void;
}

interface MessageGroup {
    date: string;
    dateLabel: string;
    messages: ChatMessage[];
    firstIndex: number;
}

// Helper function to format date labels
const formatDateLabel = (date: string): string => {
    const messageDate = dayjs(date);
    const today = dayjs();
    const yesterday = today.subtract(1, 'day');
    
    if (messageDate.isSame(today, 'day')) {
        return 'Today';
    } else if (messageDate.isSame(yesterday, 'day')) {
        return 'Yesterday';
    } else {
        return messageDate.format('MMMM D, YYYY');
    }
};

// Helper function to group messages by date
const groupMessagesByDate = (messages: ChatMessage[]): MessageGroup[] => {
    const groups: { [key: string]: { messages: ChatMessage[], firstIndex: number } } = {};
    
    messages.forEach((message, index) => {
        const dateKey = dayjs(message.created_at).format('YYYY-MM-DD');
        if (!groups[dateKey]) {
            groups[dateKey] = { messages: [], firstIndex: index };
        }
        groups[dateKey].messages.unshift(message);
    });
    
    return Object.entries(groups)
        .map(([date, { messages, firstIndex }]) => ({
            date,
            dateLabel: formatDateLabel(date),
            messages, // 保持原始顺序，不进行排序
            firstIndex
        }))
        .sort((a, b) => a.firstIndex - b.firstIndex); // 按照第一条消息在原数组中的位置排序
};

// 简化版的输入框组件
export const ChatInput: React.FC<{
    onSend: (message: string, files: FileItem[]) => void;
    isSearching: boolean;
    onStopSearch?: () => void;
}> = ({
    onSend, isSearching, onStopSearch 
}) => {
    const [inputValue, setInputValue] = useState('');
    const [uploadedFiles, setUploadedFiles] = useState<FileItem[]>([]);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const handleSubmit = () => {
        if (inputValue.trim() && !isSearching) {
            onSend(inputValue.trim(), uploadedFiles);
            setInputValue('');
            setUploadedFiles([]);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
        }
    };

    return (
        <div className={styles.chatInput}>
            <div className={styles.promptList}>
                {["I'm curious about", "I’d like to learn more about", "Keep me update on"].map(item => (
                    <div
                        className={styles.prompt} 
                        onClick={() => {
                            setInputValue(item + ' ')
                            textareaRef.current?.focus();
                        }}
                        key={item}
                    >
                        <Icon name="edit_product" />
                        {item}
                        ...
                    </div>
                ))}
            </div>
            <div className={styles.inputContainer}>
                <textarea
                    ref={textareaRef}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="What's on your mind for the next pulse?"
                    className={styles.textarea}
                    rows={1}
                    autoFocus
                    disabled={isSearching}
                />
                <div className={styles.inputActions}>
                    {!isSearching ? (
                        <button
                            onClick={handleSubmit}
                            disabled={!inputValue.trim()}
                            className={styles.sendButton}
                            type="button"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                <path d="M7.83191 12.833C7.83191 13.2932 7.45881 13.6663 6.99857 13.6663C6.53833 13.6663 6.16524 13.2932 6.16524 12.833L6.16524 1.16634C6.16524 0.706104 6.53834 0.333008 6.99857 0.333008C7.45881 0.333008 7.83191 0.706104 7.83191 1.16634L7.83191 12.833Z" fill="inherit" />
                                <path d="M13.4212 6.41052C13.7466 6.73596 13.7466 7.26347 13.4212 7.58891C13.0957 7.91434 12.5682 7.91434 12.2428 7.58891L6.99864 2.34476L1.75449 7.58891C1.42906 7.91434 0.901546 7.91434 0.576109 7.58891C0.250672 7.26347 0.250672 6.73596 0.576109 6.41052L6.40944 0.577187C6.73488 0.25175 7.26239 0.25175 7.58783 0.577187L13.4212 6.41052Z" fill="inherit" />
                            </svg>
                        </button>
                    ) : (
                        <Tooltip content="Stop">
                            <button
                                onClick={onStopSearch}
                                className={styles.stopButton}
                                type="button"
                                title="Stop"
                            >
                                <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M0 14C0 6.26801 6.26801 0 14 0C21.732 0 28 6.26801 28 14C28 21.732 21.732 28 14 28C6.26801 28 0 21.732 0 14Z" fill="black" fillOpacity="0.16" />
                                    <rect x="9" y="9" width="10" height="10" rx="2" fill="#636E83" />
                                </svg>
                            </button>
                        </Tooltip>
                    )}
                </div>
            </div>
        </div>
    );
};

// 滑动提示组件
const ScrollHint: React.FC<{
    type: 'history' | 'goToNow';
    setShowSection: (section: 'history' | 'current') => void;
}> = ({ type, setShowSection }) => {
    return (
        <div
            className={styles.scrollHintContent}
            onClick={()=>{
                if (type === 'history') {
                    setShowSection('history');
                } else {
                    setShowSection('current');
                }
            }}
        >
            {type === 'history' ? (
                <>
                    <span>History</span>
                    <Icon name="expandUp" size={16} color="#666F8D" />
                </>
            ) : (
                <>
                    <span>Go to Now</span>
                    <Icon name="expandUp" size={16} color="#666F8D" style={{transform: 'rotate(180deg)'}} />
                </>
            )}
        </div>
    );
};

export const ChatModal: React.FC<ChatModalProps> = ({ isOpen, onClose }) => {
    const { profile,openPricingModal } = useAuth();
    
    // 本地状态管理
    const [localMessages, setLocalMessages] = useState<ChatMessage[]>([]);
    const [historyMessages, setHistoryMessages] = useState<ChatMessage[]>([]);
    const [isAtTop, setIsAtTop] = useState(true);
    const [isAtBottom, setIsAtBottom] = useState(true);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [currentStreamingMessage, setCurrentStreamingMessage] = useState<string>('');
    const [abortController, setAbortController] = useState<AbortController | null>(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isLoadingConversation, setIsLoadingConversation] = useState(false);
    const [showSection, setShowSection] = useState<'history' | 'current'>('current');
    const currentSectionRef = useRef<HTMLDivElement>(null);
    const historySectionRef = useRef<HTMLDivElement>(null);
    const hasHistory = historyMessages.length > 0;
    // 加载历史记录
    const loadHistory = useCallback(async (offset = 0) => {
        if (isLoadingHistory) return;
        
        setIsLoadingHistory(true);
        try {
            // 使用当前历史消息数量作为offset来实现分页
            const response = await agentService.getPulseHistory({ 
                limit: 20, 
                offset: offset,
                order_desc: true 
            });
            setHistoryMessages(response.messages);
        } catch (error) {
            console.error('Failed to load history:', error);
        } finally {
            setIsLoadingHistory(false);
        }
    }, [isLoadingHistory, setIsLoadingHistory, setHistoryMessages])

    const loadHistoryRef = useRef<any>(null);
    useEffect(() => {
        loadHistoryRef.current = loadHistory;
    }, [loadHistory])
    
    useEffect(() => {
        loadHistoryRef.current();
    }, [isOpen])
    
    // 处理滚动事件监听
    const handleCurrentScroll = useCallback(() => {
        const element = currentSectionRef.current;
        if (!element) return;
        
        const {scrollTop} = element;
        const isTop = scrollTop === 0;
        
        setIsAtTop(isTop);
    }, []);
    
    const handleHistoryScroll = useCallback(() => {
        const element = historySectionRef.current;
        if (!element) return;
        const {scrollTop} = element;
        const isBottom = scrollTop >= 0;
        
        setIsAtBottom(isBottom);
    }, []);

    // Leading edge throttle 控制
    const wheelStateRef = useRef({
        canExecute: true,
        timeoutId: null as NodeJS.Timeout | null
    });
    const DELAY = 50; // 300ms延迟期间
    
    // 处理鼠标滚轮事件（用于内容不足以滚动的情况）
    const handleWheelCallback = useCallback((event: WheelEvent) => {
        
        // 清除之前的定时器
        if (wheelStateRef.current.timeoutId) {
            clearTimeout(wheelStateRef.current.timeoutId);
        }
        
        // 如果可以执行，立即执行并设置延迟
        if (wheelStateRef.current.canExecute) {
            if(event.deltaY < 0 && isAtTop && hasHistory){
                setShowSection('history');
            }else if(event.deltaY > 0 && isAtBottom){
                setShowSection('current');
            }
            
            // 设置为不可执行状态
            wheelStateRef.current.canExecute = false;
        }
        
        // 每次wheel事件都重新设置延迟定时器
        wheelStateRef.current.timeoutId = setTimeout(() => {
            wheelStateRef.current.canExecute = true;
            wheelStateRef.current.timeoutId = null;
        }, DELAY);

        handleCurrentScroll();
        handleHistoryScroll();
    }, [isAtTop, isAtBottom, handleCurrentScroll, handleHistoryScroll, hasHistory]);

    useEffect(() => {
        document.addEventListener('wheel', handleWheelCallback);
        
        return () => {
            document.removeEventListener('wheel', handleWheelCallback);
            // 清理定时器
            if (wheelStateRef.current.timeoutId) {
                clearTimeout(wheelStateRef.current.timeoutId);
            }
        };
    }, [handleWheelCallback]);



    useEffect(() => {
        setLocalMessages([]);
    }, [isOpen]);
    
    useEffect(() => {
        if (showSection === 'history') {
            handleHistoryScroll();
        }
    }, [historyMessages, handleHistoryScroll, showSection]);


    // 初始化本地消息
    useEffect(() => {
        setLocalMessages([]);
        setShowSection('current');
    }, []);


    const handleSend = useCallback(async (message: string, files: FileItem[]) => {
        if (!profile?.id || isGenerating) return;
        
        // 添加用户消息到本地状态
        const userMessage: ChatMessage = {
            id: `user-${Date.now()}`,
            user_id: profile.id,
            content: message,
            role: 'user',
            files: files,
            modality: 'text',
            json_content: null,
            ordering: localMessages.length + 1,
            created_at: new Date().toISOString()
        };
        
        setLocalMessages(prev => [...prev, userMessage]);
        setIsGenerating(true);
        setCurrentStreamingMessage('');
        
        // 创建新的 AbortController
        const controller = new AbortController();
        setAbortController(controller);
        
        try {
            // 用于保存完整的流式消息内容
            let fullStreamContent = '';
            let actionButton: any[] = [];
            await agentService.pulseMessage(
                {
                    content: message,
                    stream: true
                },
                (data: any) => {
                    // Handle streaming message data
                    if (data.event_type === EVENT_TYPES.SYSTEM_MESSAGE_DELTA && data.content) {
                        fullStreamContent = data.content;
                        setCurrentStreamingMessage(data.content);
                    }
                    if(data.event_type === EVENT_TYPES.ACTION_BUTTON){
                        actionButton = [data];
                    }
                },
                (error: any) => {
                    console.error('Message error:', error);
                    setCurrentStreamingMessage('');
                    setIsGenerating(false);
                    setAbortController(null);
                },
                controller.signal
            );
            
            // When streaming completes, create final message using the captured content
            const assistantMessage: ChatMessage = {
                id: `assistant-${Date.now()}`,
                user_id: profile.id,
                content: fullStreamContent, // 使用保存的完整内容而不是状态值
                role: 'assistant',
                files: [],
                modality: 'text',
                json_content: null,
                ordering: localMessages.length + 2,
                created_at: new Date().toISOString()
            };
             
            setLocalMessages(prev => [...prev, assistantMessage, ...actionButton]);
            setCurrentStreamingMessage(''); // 清空流式消息，为下次做准备
            setIsGenerating(false);
            setAbortController(null);
             
        } catch (error: any) {
            if (error?.name !== 'AbortError') {
                console.error('Failed to send message:', error);
            }
            setCurrentStreamingMessage('');
            setIsGenerating(false);
            setAbortController(null);
        }
    }, [profile?.id, isGenerating, localMessages.length]);

    if (!isOpen) return null;
    return (
        <div className={styles.overlay} onClick={onClose}>
            <div className={styles.chatModal} onClick={(e) => e.stopPropagation()}>
                <div className={styles.header}>
                    {showSection === 'current' && isAtTop && hasHistory ? <ScrollHint type="history" setShowSection={setShowSection} /> : null}
                    {showSection === 'history' && isAtBottom ? <ScrollHint type="goToNow" setShowSection={setShowSection} /> : null}
                    <button onClick={onClose} className={styles.closeButton}>
                        <Cross2Icon />
                    </button>
                </div>
                
                <div className={styles.content}>
                    <div className={cls(styles.dualSectionContainer, styles[showSection])}>
                        <div className={styles.historySection} ref={historySectionRef}>
                            <div className={styles.messagesContent}>                                
                                {/* 历史消息 */}
                                {groupMessagesByDate(historyMessages).map((group) => (
                                    <div key={group.date}>
                                        <div className={styles.dateSeparator}>
                                            <span className={styles.dateLabel}>{group.dateLabel}</span>
                                        </div>
                                        {group.messages.map((message) => (
                                            <div key={message.id} className={styles.currentMessage}>
                                                <div className={`${styles.message} ${styles[message.role]}`}>
                                                    <Markdown
                                                        content={message.content}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className={styles.currentSection} ref={currentSectionRef}>
                            <div className={styles.messagesContent}>
                                {/* 当前消息或欢迎语句 */}
                                {localMessages.length === 0 && !isLoadingConversation && (
                                    <div className={styles.welcomeMessage}>
                                        <h1 className={styles.mainTitle}>🙌🏻 Tell me what matters to your business — I'll track it daily.</h1>
                                        <p className={styles.subtitle}>
                                            This is your command center.
                                            <br />
                                            Simply tell me what you want to focus on for your next pulse.
                                        </p>
                                    </div>
                                )}
                                
                                {/* 本地消息 */}
                                {localMessages.map((message) => {
                                    // @ts-ignore
                                    if(message.event_type === 'action.button'){
                                        return (
                                            <Button 
                                                key={message.id}
                                                style={{width: 'fit-content', marginLeft: 16}}
                                                onClick={()=>{
                                                    onClose();
                                                    openPricingModal();
                                                }}
                                            >
                                                Upgrade
                                            </Button>
                                        )
                                    }
                                    return (
                                        <div key={message.id} className={styles.currentMessage}>
                                            <div className={`${styles.message} ${styles[message.role]}`}>
                                                <Markdown
                                                    content={message.content}
                                                />
                                            </div>
                                        </div>
                                    )})}
                                
                                {/* 流式消息 */}
                                {currentStreamingMessage ? (
                                    <div className={styles.currentMessage}>
                                        <div className={`${styles.message} ${styles.assistant}`}>
                                            <Markdown
                                                content={currentStreamingMessage}
                                            />
                                        </div>
                                    </div>
                                ) : null}
                                {isGenerating && !currentStreamingMessage ? (
                                    <Lottie
                                        animationData={GeneratingLottie}
                                        loop={true}
                                        className={styles.generatingLottie}
                                    />
                                ) : null}
                            </div>
                        </div>
                    </div>
                </div>
                
                <ChatInput
                    onSend={handleSend}
                    isSearching={isGenerating}
                    onStopSearch={() => {
                        if (abortController) {
                            abortController.abort();
                            setAbortController(null);
                            setIsGenerating(false);
                            setCurrentStreamingMessage('');
                        }
                    }}
                />
            </div>
        </div>
    )
}